<script setup lang="ts">
import {
  computed,
  ref,
  inject,
  watch,
  onMounted,
  onUnmounted,
  toValue,
  type ComponentPublicInstance,
  type Ref,
} from 'vue';
import { useMediaQuery } from '@vueuse/core';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import Avatar from 'primevue/avatar';
import AvatarGroup from 'primevue/avatargroup';
import CandidateAvatar from '@/components/CandidateAvatar.vue';
import ExchangeBadge from './ExchangeBadge.vue';
import SectionIndexScrubber from './SectionIndexScrubber.vue';
import StrikeExchangeDrawer from '@/components/StrikeExchangeDrawer.vue';
import StrikeExchangePopover from '@/components/StrikeExchangePopover.vue';
import SpeedBumpDrawer from '@/components/SpeedBumpDrawer.vue';
import { useSpeedBump } from '@/composables/useSpeedBump';
import type {
  ForecastTraderElectionGridProduct,
  ForecastTraderElectionStateGridState,
} from '@/types/forecasttrader.types';
import type { PositionData } from '@/types/accountPnl.types';
import type { ElectionOffice } from '@/types/forecasttrader.types';
import type { StrikeGroup } from '@/types/contracts.types';
import type { PredictionContractListContract } from '@/types/prediction.types';
import type { CandidateTableRow } from '../types/election.types';
import { STATE_NAMES, type USStateCode } from '../types/election.types';
import {
  transformApiResponseToTableRows,
  transformApiProductToContract,
  transformContractToCandidates,
  transformCandidateToTableRow,
} from '../utils/apiTransformers';
import { filterTopTwoCandidates } from '../utils/leadingCandidate';
import { getBallotpediaUrl } from '../utils/partyUtils';
import { allYesConids, allNoConids } from '../utils/matchedConids';
import { useNavigation } from '@/composables/useNavigation';
import { formatDollarPrice } from '@/utils/formatPrice';
import { formatCompactNumber } from '@/utils/formatNumber';
import { resolveCloseConid } from '@/utils/closeConid';
import { useOrderTicket } from '../composables/useOrderTicket';
import { useUserState } from '@/stores/useUserState';
import { useVisibilityStreamedMdData } from '../composables/useVisibilityPolledMdData';
import { useRaceOiStream } from '../composables/useRaceOiStream';
import { buildPredictionMarketsUrl, ELECTION_BOARD_TAB_KEY } from '../utils/predictionMarketsNavigation';

interface Props {
  products: ForecastTraderElectionGridProduct[];
  loading: boolean;
  /** When undefined (All By State), office is derived per-product from its name */
  office?: ElectionOffice;
  positionsMap: Record<number, PositionData>;
  /** Structured state data for "All By State" mode — enables state header rows */
  stateData?: ForecastTraderElectionStateGridState[];
  /** When true, all states start expanded (e.g. positions filter active) */
  allStatesExpanded?: boolean;
  /** Current sort mode — scrubber only shown for alphabetical */
  sortBy?: 'alphabetical' | 'open-interest';
  /**
   * FTAPI-231: product conids (e.g. balance-of-power's Federal Race Outcomes /
   * US House/Senate Control) that have no election-race details to show in
   * ElectionDetailsPanel. Clicking their race/candidate info links out to
   * Prediction Markets (same as "see more contracts") instead of opening it.
   */
  linkedProductConids?: number[];
}

interface Emits {
  (e: 'select-race', productConid: number): void;
  (e: 'select-candidate', payload: { productConid: number; conidYes: number }): void;
  (e: 'share', conidYes: number): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const isDesktopPopover = useMediaQuery('(min-width: 1280px)');
const isMobileScrubber = computed(() => !isDesktopPopover.value);
const { openOrderTicket: _openOrderTicket } = useOrderTicket();
const userState = useUserState();
const speedBump = useSpeedBump();

const drawerVisible = ref(false);
const drawerStrikeGroups = ref<StrikeGroup[]>([]);
const drawerMarketName = ref('');
const drawerInitialStrike = ref('');

const popoverRef = ref<InstanceType<typeof StrikeExchangePopover> | null>(null);
const popoverContracts = ref<PredictionContractListContract[]>([]);
const popoverStrikeLabel = ref('');
const popoverSide = ref<'yes' | 'no'>('yes');
const popoverIntent = ref<string | undefined>();

const candidateMenuRef = ref<InstanceType<typeof Menu> | null>(null);
const candidateMenuItems = ref<{ label: string; icon: string; command: () => void }[]>([]);
const { openUrl } = useNavigation();
const electionBoardTab = inject(ELECTION_BOARD_TAB_KEY, undefined);

function openCandidateMenu(event: Event, candidateName: string, conidYes: number) {
  const items: { label: string; icon: string; command: () => void }[] = [];
  const bpUrl = getBallotpediaUrl(candidateName);
  if (bpUrl) {
    items.push({
      label: 'Ballotpedia',
      icon: 'ibi-arrow-up-right',
      command: () => openUrl(bpUrl, { target: '_blank' }),
    });
  }
  items.push({ label: 'Share', icon: 'ibi-share-send', command: () => emit('share', conidYes) });
  candidateMenuItems.value = items;
  candidateMenuRef.value?.toggle(event);
}

function openPredictionMarkets(raceId: string) {
  const race = groupedByRace.value.get(raceId)?.[0];
  if (!race?.productConid || !race.exchange) return;
  openUrl(
    buildPredictionMarketsUrl({
      productConid: race.productConid,
      exchange: race.exchange,
      origin: 'table',
      view: 'grid',
      tab: electionBoardTab?.value,
    }),
  );
}

// FTAPI-231: linkedProductConids has no election-race details for these products
// (ElectionDetailsPanel expects real race/candidate data), so route their row
// clicks to Prediction Markets instead of the normal select-race/select-candidate emits.
const linkedProductConidSet = computed(() => new Set(props.linkedProductConids ?? []));

function handleRaceClick(raceId: string) {
  const productConid = groupedByRace.value.get(raceId)?.[0]?.productConid;
  if (productConid && linkedProductConidSet.value.has(productConid)) {
    openPredictionMarkets(raceId);
    return;
  }
  emit('select-race', productConid ?? 0);
}

function handleCandidateClick(candidate: CandidateTableRow) {
  if (linkedProductConidSet.value.has(candidate.productConid)) {
    openPredictionMarkets(candidate.raceId);
    return;
  }
  emit('select-candidate', { productConid: candidate.productConid, conidYes: candidate.conid_yes });
}

function buildStrikeGroupsForRace(candidateConidYes: number): StrikeGroup[] {
  const candidate = candidateRows.value.find((c) => c.conid_yes === candidateConidYes);
  if (!candidate) return [];
  const raceCandidates = groupedByRace.value.get(candidate.raceId) || [];

  const groups: StrikeGroup[] = [];
  for (const c of raceCandidates) {
    const contracts: PredictionContractListContract[] = [];
    const exchanges: string[] = [];
    for (const product of activeProducts.value) {
      for (const contract of product.contracts) {
        if (contract.conid_yes === c.conid_yes) {
          contracts.push({
            conid_yes: contract.conid_yes,
            conid_no: contract.conid_no,
            exchange: contract.exchange,
            strike_label: c.candidateName,
            underlying_conid: c.productConid,
            product_conid: c.productConid,
          });
          exchanges.push(contract.exchange);
          if (contract.matched) {
            for (const m of contract.matched) {
              contracts.push({
                conid_yes: m.conid_yes,
                conid_no: m.conid_no,
                exchange: m.exchange,
                strike_label: c.candidateName,
                underlying_conid: c.productConid,
                product_conid: c.productConid,
              });
              exchanges.push(m.exchange);
            }
          }
          break;
        }
      }
      if (contracts.length > 0) break;
    }
    if (contracts.length > 0) {
      groups.push({
        strike_label: c.candidateName,
        primary: contracts[0],
        contracts,
        exchanges,
        expandable: contracts.length > 1,
      });
    }
  }
  return groups;
}

function buildContractsForCandidate(candidateConidYes: number): PredictionContractListContract[] {
  const candidate = candidateRows.value.find((c) => c.conid_yes === candidateConidYes);
  if (!candidate) return [];
  const contracts: PredictionContractListContract[] = [];
  for (const product of activeProducts.value) {
    for (const contract of product.contracts) {
      if (contract.conid_yes === candidateConidYes) {
        contracts.push({
          conid_yes: contract.conid_yes,
          conid_no: contract.conid_no,
          exchange: contract.exchange,
          strike_label: candidate.candidateName,
          underlying_conid: candidate.productConid,
          product_conid: candidate.productConid,
        });
        if (contract.matched) {
          for (const m of contract.matched) {
            contracts.push({
              conid_yes: m.conid_yes,
              conid_no: m.conid_no,
              exchange: m.exchange,
              strike_label: candidate.candidateName,
              underlying_conid: candidate.productConid,
              product_conid: candidate.productConid,
            });
          }
        }
        break;
      }
    }
    if (contracts.length > 0) break;
  }
  return contracts;
}

function openOrderTicket(conid: number, candidateConidYes?: number, intent?: string, event?: Event) {
  const exchanges = candidateConidYes ? productLookups.value.candidateExchanges[candidateConidYes] : undefined;
  const hasMultipleExchanges = (exchanges?.length ?? 0) > 1;
  const candidate = candidateConidYes ? candidateRows.value.find((c) => c.conid_yes === candidateConidYes) : undefined;
  const hasPosition = candidate ? getPositionSide(candidate.conid_yes, candidate.conid_no) !== null : false;
  if (hasMultipleExchanges && hasPosition) {
    const conidMatchesYes = candidateConidYes !== undefined && conid === candidateConidYes;
    const isYesSide = intent === 'close' ? !conidMatchesYes : conidMatchesYes;
    if (isDesktopPopover.value && event) {
      const contracts = buildContractsForCandidate(candidateConidYes!);
      if (contracts.length > 0) {
        popoverContracts.value = contracts;
        popoverStrikeLabel.value = candidate?.candidateName ?? '';
        popoverSide.value = isYesSide ? 'yes' : 'no';
        popoverIntent.value = intent;
        console.log('popover open', {
          conid,
          candidateConidYes,
          intent,
          isYesSide,
          side: popoverSide.value,
          contracts: contracts.map((c) => ({ exchange: c.exchange, conid_yes: c.conid_yes, conid_no: c.conid_no })),
        });
        popoverRef.value?.toggle(event);
        return;
      }
    }
    const groups = buildStrikeGroupsForRace(candidateConidYes!);
    if (groups.length > 0) {
      drawerStrikeGroups.value = groups;
      drawerMarketName.value = candidate?.raceName ?? '';
      drawerInitialStrike.value = candidate?.candidateName ?? '';
      drawerVisible.value = true;
      return;
    }
  }

  const exchange = hasMultipleExchanges ? 'BEST' : exchanges?.[0];
  const isYesSide = candidateConidYes !== undefined && conid === candidateConidYes;
  const pairsMap = isYesSide ? productLookups.value.yesConidExPairsMap : productLookups.value.noConidExPairsMap;
  const eventConidExs = candidateConidYes ? pairsMap[candidateConidYes] : undefined;
  let closeConid = conid;
  if (intent === 'close' && candidate) {
    closeConid = resolveCloseConid({
      exchange,
      forecastxConid: isYesSide ? candidate.conid_no : candidate.conid_yes,
      defaultConid: conid,
    });
  }
  _openOrderTicket(closeConid, intent, exchange, eventConidExs);
}

function handleDrawerBuyYes(contract: PredictionContractListContract, exchange?: string) {
  const group = drawerStrikeGroups.value.find((g) => g.contracts.some((c) => c.conid_yes === contract.conid_yes));
  if (
    group &&
    exchange &&
    speedBump.intercept({ group, targetExchange: exchange, side: 'yes', conid: contract.conid_yes })
  ) {
    drawerVisible.value = false;
    return;
  }
  _openOrderTicket(contract.conid_yes, undefined, exchange);
  drawerVisible.value = false;
}

function handleDrawerBuyNo(contract: PredictionContractListContract, exchange?: string) {
  const group = drawerStrikeGroups.value.find((g) => g.contracts.some((c) => c.conid_yes === contract.conid_yes));
  if (
    group &&
    exchange &&
    speedBump.intercept({ group, targetExchange: exchange, side: 'no', conid: contract.conid_no })
  ) {
    drawerVisible.value = false;
    return;
  }
  _openOrderTicket(contract.conid_no, undefined, exchange);
  drawerVisible.value = false;
}

function handleDrawerCloseYes(contract: PredictionContractListContract, exchange?: string) {
  const conid = resolveCloseConid({
    exchange,
    forecastxConid: contract.conid_yes,
    defaultConid: contract.conid_no,
  });
  _openOrderTicket(conid, 'close', exchange);
  drawerVisible.value = false;
}

function handleDrawerCloseNo(contract: PredictionContractListContract, exchange?: string) {
  const conid = resolveCloseConid({
    exchange,
    forecastxConid: contract.conid_no,
    defaultConid: contract.conid_yes,
  });
  _openOrderTicket(conid, 'close', exchange);
  drawerVisible.value = false;
}

function handlePopoverBuyYes(contract: PredictionContractListContract, exchange?: string) {
  const groups = buildStrikeGroupsForRace(contract.conid_yes);
  const group = groups.find((g) => g.contracts.some((c) => c.conid_yes === contract.conid_yes));
  if (
    group &&
    exchange &&
    speedBump.intercept({ group, targetExchange: exchange, side: 'yes', conid: contract.conid_yes })
  ) {
    return;
  }
  _openOrderTicket(contract.conid_yes, undefined, exchange);
}

function handlePopoverBuyNo(contract: PredictionContractListContract, exchange?: string) {
  const groups = buildStrikeGroupsForRace(contract.conid_yes);
  const group = groups.find((g) => g.contracts.some((c) => c.conid_yes === contract.conid_yes));
  if (
    group &&
    exchange &&
    speedBump.intercept({ group, targetExchange: exchange, side: 'no', conid: contract.conid_no })
  ) {
    return;
  }
  _openOrderTicket(contract.conid_no, undefined, exchange);
}

function handlePopoverCloseYes(contract: PredictionContractListContract, exchange?: string) {
  const conid = resolveCloseConid({
    exchange,
    forecastxConid: contract.conid_yes,
    defaultConid: contract.conid_no,
  });
  _openOrderTicket(conid, 'close', exchange);
}

function handlePopoverCloseNo(contract: PredictionContractListContract, exchange?: string) {
  const conid = resolveCloseConid({
    exchange,
    forecastxConid: contract.conid_no,
    defaultConid: contract.conid_yes,
  });
  _openOrderTicket(conid, 'close', exchange);
}

/** Hub sticky header height (provided by ElectionHub) */
const hubStickyHeight = inject<Ref<number>>('stickyOffset', ref(0));

/**
 * Resolve the effective scroll container.
 * In native webviews RATIO_SCROLL_CONTAINER is the scrollable surface;
 * on web the hub overrides it to overflow:visible so the window scrolls.
 */
function getScrollContainer(): HTMLElement | null {
  const el = document.getElementById('RATIO_SCROLL_CONTAINER');
  return el && el.scrollHeight > el.clientHeight ? el : null;
}

/**
 * Total height obscured by sticky headers above the table.
 * Combines the host app's --padding-top (forecast.trader chrome) with
 * the hub's own sticky header measured via ResizeObserver.
 */
function getStickyOffset(): number {
  const paddingTop = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--padding-top')) || 0;
  return paddingTop + hubStickyHeight.value;
}
// ---------------------------------------------------------------------------
// Data transformation: raw products → CandidateTableRow[] → grouped by race
// ---------------------------------------------------------------------------

/** Derive office from product name for mixed-office mode (All By State) */
function deriveOffice(name: string): ElectionOffice {
  const lower = name.toLowerCase();
  if (lower.includes('governor') || lower.includes('gubernatorial')) return 'governor';
  if (lower.includes('house') || lower.includes('representative')) return 'house';
  return 'senate';
}

/** Map state name to two-letter code */
function mapStateNameToCode(stateName: string): USStateCode {
  const normalized = stateName.trim();
  for (const [code, name] of Object.entries(STATE_NAMES)) {
    if (name === normalized) return code as USStateCode;
  }
  return normalized.slice(0, 2).toUpperCase() as USStateCode;
}

/**
 * In state-grouped mode, only process products from expanded states.
 * This gates ALL downstream computations (rows, lookups, OI queries, MD polling)
 * so collapsed states generate zero network requests.
 */
const activeProducts = computed(() => {
  if (!props.stateData) return props.products;
  const expanded = expandedStates.value;
  if (expanded.size === 0) return [];
  return props.stateData
    .filter((state) => expanded.has(mapStateNameToCode(state.name)))
    .flatMap((state) => state.products);
});

const candidateRows = computed<CandidateTableRow[]>(() => {
  if (activeProducts.value.length === 0) return [];
  if (props.office) {
    return transformApiResponseToTableRows({ products: activeProducts.value }, props.office);
  }
  // Mixed-office mode: derive office per product
  return activeProducts.value.flatMap((product) => {
    const office = deriveOffice(product.name);
    const apiContract = transformApiProductToContract(product, office);
    const candidates = transformContractToCandidates(apiContract);
    return candidates.map(transformCandidateToTableRow);
  });
});

const groupedByRace = computed(() => {
  const map = new Map<string, CandidateTableRow[]>();
  candidateRows.value.forEach((c) => {
    const existing = map.get(c.raceId) || [];
    existing.push(c);
    map.set(c.raceId, existing);
  });
  return map;
});

// Map each primary contract conid to all of its same-side sibling conids (primary +
// matched exchanges). Position checks key off the primary conid in templates, but the
// actual position may be held on a matched venue (e.g. KALSHI), so we resolve siblings.
const siblingConids = computed(() => {
  const yesSiblings = new Map<number, number[]>();
  const noSiblings = new Map<number, number[]>();
  candidateRows.value.forEach((c) => {
    yesSiblings.set(c.conid_yes, allYesConids(c));
    noSiblings.set(c.conid_no, allNoConids(c));
  });
  return { yesSiblings, noSiblings };
});

// Ordered race IDs to preserve API order
const raceIds = computed(() => {
  const seen = new Set<string>();
  const ids: string[] = [];
  candidateRows.value.forEach((c) => {
    if (!seen.has(c.raceId)) {
      seen.add(c.raceId);
      ids.push(c.raceId);
    }
  });
  return ids;
});

// ---------------------------------------------------------------------------
// State grouping (All By State mode) — collapsed by default
// ---------------------------------------------------------------------------

interface StateHeaderInfo {
  stateCode: USStateCode;
  stateName: string;
  raceCount: number;
  raceIds: string[];
}

const expandedStates = ref(new Set<string>());

// When allStatesExpanded changes, always apply immediately.
// When stateData changes (race type switch, etc.), re-expand all if flag is active, else collapse.
// Price refreshes (same states/products) are ignored via key comparison.
let prevStateKey: string | null = null;
watch(
  () => [props.stateData, props.allStatesExpanded] as const,
  ([data, expandAll]) => {
    const dataKey = data ? data.map((s) => `${s.name}:${s.products.map((p) => p.conid).join('+')}`).join(',') : '';
    const key = `${dataKey}|${expandAll}`;
    if (key !== prevStateKey) {
      expandedStates.value = expandAll && data ? new Set(data.map((s) => mapStateNameToCode(s.name))) : new Set();
      prevStateKey = key;
    }
  },
);

function toggleStateExpansion(stateCode: string) {
  const next = new Set(expandedStates.value);
  if (next.has(stateCode)) {
    next.delete(stateCode);
  } else {
    next.add(stateCode);
  }
  expandedStates.value = next;
}

/** Map productConid → stateCode from stateData prop */
const productConidToState = computed(() => {
  const map = new Map<number, string>();
  if (!props.stateData) return map;
  for (const state of props.stateData) {
    const stateCode = mapStateNameToCode(state.name);
    for (const product of state.products) {
      map.set(product.conid, stateCode);
    }
  }
  return map;
});

/** State headers — race count from full stateData, raceIds from active (expanded) set */
const stateHeaders = computed<StateHeaderInfo[]>(() => {
  if (!props.stateData) return [];
  const pToS = productConidToState.value;
  // Bucket active raceIds by state code (only expanded states have active races)
  const stateRaceMap = new Map<string, string[]>();
  for (const raceId of raceIds.value) {
    const first = groupedByRace.value.get(raceId)?.[0];
    if (!first) continue;
    const stateCode = pToS.get(first.productConid);
    if (!stateCode) continue;
    const arr = stateRaceMap.get(stateCode) || [];
    arr.push(raceId);
    stateRaceMap.set(stateCode, arr);
  }
  // Build headers in API state order — raceCount from raw stateData (always visible),
  // raceIds from active set (only populated when expanded)
  return props.stateData
    .filter((state) => state.products.length > 0)
    .map((state) => {
      const stateCode = mapStateNameToCode(state.name);
      // Count unique products (races) from the full state data
      const uniqueProducts = new Set(state.products.map((p) => p.conid));
      return {
        stateCode,
        stateName: state.name,
        raceCount: uniqueProducts.size,
        raceIds: stateRaceMap.get(stateCode) || [],
      };
    });
});

// ---------------------------------------------------------------------------
// A-Z scrubber for mobile
// ---------------------------------------------------------------------------

const scrubberSections = computed(() => {
  const seen = new Set<string>();
  const sections: Array<{ letter: string; elementId: string }> = [];

  if (stateHeaders.value.length > 0) {
    // State-grouped mode: use state names
    for (const header of stateHeaders.value) {
      const letter = header.stateName[0].toUpperCase();
      if (!seen.has(letter)) {
        seen.add(letter);
        sections.push({ letter, elementId: `state-anchor-${header.stateCode}` });
      }
    }
  } else {
    // Flat mode: use race state codes (first letter of state name)
    for (const raceId of raceIds.value) {
      const first = groupedByRace.value.get(raceId)?.[0];
      if (!first) continue;
      const stateName = STATE_NAMES[first.stateCode] || first.stateCode;
      const letter = stateName[0].toUpperCase();
      if (!seen.has(letter)) {
        seen.add(letter);
        sections.push({ letter, elementId: `race-anchor-${raceId}` });
      }
    }
  }

  return sections.sort((a, b) => a.letter.localeCompare(b.letter));
});

function handleScrubberNavigate(letter: string) {
  const section = scrubberSections.value.find((s) => s.letter === letter);
  if (!section) return;
  const el = document.getElementById(section.elementId);
  if (!el) return;
  const offset = getStickyOffset();
  const scrollContainer = getScrollContainer();
  if (scrollContainer) {
    const top =
      el.getBoundingClientRect().top + scrollContainer.scrollTop - scrollContainer.getBoundingClientRect().top - offset;
    scrollContainer.scrollTo({ top, behavior: 'instant' });
  } else {
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'instant' });
  }
}

// Top two candidates per race (leading DEM + GOP)
const topTwoByRace = computed(() => {
  const map = new Map<string, CandidateTableRow[]>();
  groupedByRace.value.forEach((candidates, raceId) => {
    const topTwo = filterTopTwoCandidates(candidates, candidates[0]?.leadingDemConid, candidates[0]?.leadingRepConid);
    map.set(raceId, topTwo);
  });
  return map;
});

// ---------------------------------------------------------------------------
// Single-pass product lookups (exchange, bid, OI) — avoids 6 separate loops
// ---------------------------------------------------------------------------

const productLookups = computed(() => {
  const exchangeMap: Record<number, string> = {};
  const candidateExchanges: Record<number, string[]> = {};
  const yesConidExPairsMap: Record<number, string[]> = {};
  const noConidExPairsMap: Record<number, string[]> = {};
  const bidYesMap: Record<number, number | undefined> = {};
  const oiMap: Record<number, number | undefined> = {};
  const raceOiSeedMap: Record<number, number | undefined> = {};
  const productExchangesMap: Record<number, string[]> = {};
  const strikeCountMap: Record<number, number | undefined> = {};

  for (const product of activeProducts.value) {
    raceOiSeedMap[product.conid] = product.open_interest;
    if (product.exchanges?.length) productExchangesMap[product.conid] = product.exchanges;
    strikeCountMap[product.conid] = product.strike_count;
    for (const contract of product.contracts) {
      // Exchange
      if (contract.exchange) exchangeMap[contract.conid_yes] = contract.exchange;
      const exchanges: string[] = [];
      const yesPairs: string[] = [];
      const noPairs: string[] = [];
      if (contract.exchange) {
        exchanges.push(contract.exchange);
        yesPairs.push(`${contract.conid_yes}@${contract.exchange}`);
        noPairs.push(`${contract.conid_no}@${contract.exchange}`);
      }
      if (contract.matched) {
        for (const m of contract.matched) {
          if (m.exchange) {
            exchangeMap[m.conid_yes] = m.exchange;
            exchanges.push(m.exchange);
            yesPairs.push(`${m.conid_yes}@${m.exchange}`);
            noPairs.push(`${m.conid_no}@${m.exchange}`);
          }
        }
      }
      if (exchanges.length) candidateExchanges[contract.conid_yes] = exchanges;
      if (yesPairs.length > 1) yesConidExPairsMap[contract.conid_yes] = yesPairs;
      if (noPairs.length > 1) noConidExPairsMap[contract.conid_yes] = noPairs;

      // Bid
      bidYesMap[contract.conid_yes] = contract.bid_yes;
      bidYesMap[contract.conid_no] = contract.bid_no;

      // OI
      oiMap[contract.conid_yes] = contract.open_interest;
    }
  }
  return {
    exchangeMap,
    candidateExchanges,
    yesConidExPairsMap,
    noConidExPairsMap,
    bidYesMap,
    oiMap,
    raceOiSeedMap,
    productExchangesMap,
    strikeCountMap,
  };
});

// Unique exchanges per race — the product-level `exchanges` list covers venues whose
// contracts aren't returned per-candidate; fall back to contract + matched exchanges.
const raceExchanges = computed(() => {
  const map = new Map<string, string[]>();
  const { candidateExchanges, productExchangesMap } = productLookups.value;
  groupedByRace.value.forEach((candidates, raceId) => {
    const productExchanges = productExchangesMap[candidates[0]?.productConid];
    if (productExchanges) {
      map.set(raceId, productExchanges);
      return;
    }
    const set = new Set<string>();
    for (const c of candidates) {
      const exs = candidateExchanges[c.conid_yes];
      if (exs) exs.forEach((e) => set.add(e));
    }
    if (set.size) map.set(raceId, Array.from(set));
  });
  return map;
});

// ---------------------------------------------------------------------------
// Race-level open interest from mdfarm API (bulk upfront fetch for all races)
// ---------------------------------------------------------------------------

const isPublicApi = computed(() => toValue(userState.usePublicApi));

// Precomputed per-race derived data — avoids repeated function calls in template
// "See more contracts" row is gated on the race's strike_count (all exchanges) exceeding the
// top two shown; falls back to the returned contracts when strike_count is absent.
// Chips preview only the extra contracts the grid actually returned.
const raceViewData = computed(() => {
  const map = new Map<string, { visible: CandidateTableRow[]; extras: CandidateTableRow[]; hasMore: boolean }>();
  const { strikeCountMap } = productLookups.value;
  groupedByRace.value.forEach((all, raceId) => {
    const visible = topTwoByRace.value.get(raceId) ?? [];
    const visibleConids = new Set(visible.map((c) => c.conid_yes));
    const extras = all.filter((c) => !visibleConids.has(c.conid_yes));
    const strikeCount = strikeCountMap[all[0]?.productConid] ?? all.length;
    map.set(raceId, { visible, extras, hasMore: strikeCount > visible.length });
  });
  return map;
});

/** Visible candidate rows plus the "see more contracts" row when present */
function raceRowSpan(raceId: string): number {
  const data = raceViewData.value.get(raceId);
  return (data?.visible.length ?? 0) + (data?.hasMore ? 1 : 0);
}

// ---------------------------------------------------------------------------
// IntersectionObserver — race-level visibility for MD data polling
// ---------------------------------------------------------------------------

const currentlyVisibleRaceIds = ref(new Set<string>());
const renderedRaceIds = ref(new Set<string>());
const raceHeightCache = new Map<string, number>();

const raceElementMap = new Map<string, Element>();
// Two observers: tight margin for MD polling, wide margin for DOM virtualization
let pollingObserver: IntersectionObserver | null = null;
let renderObserver: IntersectionObserver | null = null;

const trackRaceElement = (raceId: string, el: Element | ComponentPublicInstance | null) => {
  const element = el instanceof Element ? el : (el as ComponentPublicInstance)?.$el;
  if (element instanceof Element) {
    const prev = raceElementMap.get(raceId);
    if (prev !== element) {
      if (prev) {
        pollingObserver?.unobserve(prev);
        renderObserver?.unobserve(prev);
      }
      raceElementMap.set(raceId, element);
      (element as HTMLElement).dataset.raceId = raceId;
      pollingObserver?.observe(element);
      renderObserver?.observe(element);
    }
  } else {
    const existing = raceElementMap.get(raceId);
    if (existing) {
      pollingObserver?.unobserve(existing);
      renderObserver?.unobserve(existing);
      raceElementMap.delete(raceId);
      const next = new Set(currentlyVisibleRaceIds.value);
      next.delete(raceId);
      currentlyVisibleRaceIds.value = next;
    }
  }
};

/** Placeholder height for an unmounted race: cached measurement or estimate */
function racePlaceholderHeight(raceId: string): string {
  const cached = raceHeightCache.get(raceId);
  if (cached) return cached + 'px';
  return (raceViewData.value.has(raceId) ? raceRowSpan(raceId) : 2) * 3.5 + 5 + 'rem';
}

onMounted(() => {
  // Defer observer start: let the browser paint server-side data first,
  // then start tracking visibility to progressively activate live MD queries/streams.
  requestAnimationFrame(() => {
    // Use RATIO_SCROLL_CONTAINER as root when it's the actual scrollable surface
    // (native webviews). On web the hub overrides it to overflow:visible, so
    // getScrollContainer() returns null and we fall back to the viewport.
    const observerRoot = getScrollContainer();

    // Tight observer: drives MD polling for races near the viewport
    pollingObserver = new IntersectionObserver(
      (entries) => {
        const next = new Set(currentlyVisibleRaceIds.value);
        let changed = false;
        for (const entry of entries) {
          const raceId = (entry.target as HTMLElement).dataset.raceId;
          if (!raceId) continue;
          if (entry.isIntersecting) {
            if (!next.has(raceId)) {
              next.add(raceId);
              changed = true;
            }
          } else {
            if (next.has(raceId)) {
              next.delete(raceId);
              changed = true;
            }
          }
        }
        if (changed) currentlyVisibleRaceIds.value = next;
      },
      { root: observerRoot, rootMargin: '150px 0px' },
    );

    // Wide observer: bidirectional render zone for DOM virtualization.
    // Races mount when entering the buffer, unmount when leaving.
    // Heights are cached before unmount so placeholders match real content.
    // Updates happen directly in the callback — Vue's microtask-based DOM
    // update completes before the browser paints, preventing blank frames.
    renderObserver = new IntersectionObserver(
      (entries) => {
        const next = new Set(renderedRaceIds.value);
        let changed = false;
        for (const entry of entries) {
          const raceId = (entry.target as HTMLElement).dataset.raceId;
          if (!raceId) continue;
          if (entry.isIntersecting) {
            if (!next.has(raceId)) {
              next.add(raceId);
              changed = true;
            }
          } else if (next.has(raceId)) {
            // Cache measured height before unmount so placeholder doesn't cause scroll jump
            const el = raceElementMap.get(raceId);
            if (el) raceHeightCache.set(raceId, (el as HTMLElement).offsetHeight);
            next.delete(raceId);
            changed = true;
          }
        }
        if (changed) renderedRaceIds.value = next;
      },
      { root: observerRoot, rootMargin: '500px 0px' },
    );

    raceElementMap.forEach((el) => {
      pollingObserver!.observe(el);
      renderObserver!.observe(el);
    });
  });
});

onUnmounted(() => {
  pollingObserver?.disconnect();
  renderObserver?.disconnect();
  raceElementMap.clear();
  raceHeightCache.clear();
});

// ---------------------------------------------------------------------------
// Live MD farm polling (bid/ask + OI), gated by viewport visibility
// ---------------------------------------------------------------------------

const visibleCandidatesByRace = computed(() => {
  const map = new Map<string, CandidateTableRow[]>();
  raceViewData.value.forEach((data, raceId) => {
    map.set(raceId, data.visible);
  });
  return map;
});

const { liveBidMap, liveOiMap } = useVisibilityStreamedMdData({
  visibleCandidatesByRace,
  currentlyVisibleRaceIds,
  yesConidExPairsMap: computed(() => productLookups.value.yesConidExPairsMap),
  noConidExPairsMap: computed(() => productLookups.value.noConidExPairsMap),
});

// Race OI entries for WS streaming (auth only) — scoped to visible races
const visibleRaceOiEntries = computed(() => {
  const visible = currentlyVisibleRaceIds.value;
  const entries: { productConid: number; exchange: string }[] = [];
  for (const raceId of raceIds.value) {
    if (!visible.has(raceId)) continue;
    const first = groupedByRace.value.get(raceId)?.[0];
    if (first) entries.push({ productConid: first.productConid, exchange: first.exchange });
  }
  return entries;
});

const { raceOiMap: wsRaceOiMap } = useRaceOiStream({ visibleRaceEntries: visibleRaceOiEntries });

const raceOiMap = computed(() => {
  const seedMap = productLookups.value.raceOiSeedMap;
  if (!isPublicApi.value) {
    // Auth: merge WS live data over seeds
    const map = new Map<string, number>();
    for (const [conid, seed] of Object.entries(seedMap)) {
      if (seed !== undefined) map.set(conid, seed);
    }
    // Overlay live WS data
    for (const [key, val] of wsRaceOiMap.value) {
      map.set(key, val);
    }
    return map;
  }
  // Non-auth: use seeds from grid API
  const map = new Map<string, number>();
  for (const [conid, seed] of Object.entries(seedMap)) {
    if (seed !== undefined) map.set(conid, seed);
  }
  return map;
});

// ---------------------------------------------------------------------------
// Formatting helpers
// ---------------------------------------------------------------------------

function resolvePrice(conid: number): number | undefined | null {
  const live = liveBidMap.value[conid];
  if (live !== undefined) return live;
  return productLookups.value.bidYesMap[conid];
}

function formatPrice(conid: number): string {
  const price = resolvePrice(conid);
  if (price === undefined || price === null || price < 0) return '\u2014';
  return `$${formatDollarPrice(price)}`;
}

function formatOI(conidYes: number): string {
  const oi = liveOiMap.value[conidYes] ?? productLookups.value.oiMap[conidYes];
  if (oi === undefined || oi === null) return '\u2014';
  return formatCompactNumber(oi);
}

function formatRaceOI(raceId: string): string {
  const productConid = groupedByRace.value.get(raceId)?.[0]?.productConid;
  const oi = productConid ? (raceOiMap.value.get(String(productConid)) ?? 0) : 0;
  return formatCompactNumber(oi);
}

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'numeric',
  day: 'numeric',
  year: '2-digit',
});

// FTAPI-231: balance-of-power races settle on the general election, not a fixed
// calendar day — prefer the service-provided measuredPeriod (e.g. "Nov'26") when set.
function formatRaceDate(lastTradeTime: number, measuredPeriod?: string): string {
  if (measuredPeriod) return measuredPeriod;
  if (!lastTradeTime) return '';
  return dateFormatter.format(new Date(lastTradeTime * 1000));
}

function getExchangeIconUrl(exchange: string): string {
  return `/tws.proxy/public/icons/forecastx?exchange=${exchange.toLowerCase()}`;
}

// Total absolute position size across the given conids (primary + matched siblings).
function positionSizeFor(conids: number[]): number {
  return conids.reduce((sum, conid) => {
    const pos = props.positionsMap[conid];
    return sum + (pos ? Math.abs(pos.position) : 0);
  }, 0);
}

// Whether any of the given conids (primary + matched siblings) holds an open position.
function hasOpenPosition(conids: number[]): boolean {
  return conids.some((conid) => {
    const pos = props.positionsMap[conid];
    return !!(pos && pos.position !== 0);
  });
}

function getPositionSide(conidYes: number, conidNo: number): 'Yes' | 'No' | null {
  if (hasOpenPosition(siblingConids.value.yesSiblings.get(conidYes) ?? [conidYes])) return 'Yes';
  if (hasOpenPosition(siblingConids.value.noSiblings.get(conidNo) ?? [conidNo])) return 'No';
  return null;
}

function isYesCloseState(conidNo: number): boolean {
  return hasOpenPosition(siblingConids.value.noSiblings.get(conidNo) ?? [conidNo]);
}

function isNoCloseState(conidYes: number): boolean {
  return hasOpenPosition(siblingConids.value.yesSiblings.get(conidYes) ?? [conidYes]);
}

function getPositionDisplay(conidYes: number, conidNo: number): string {
  const side = getPositionSide(conidYes, conidNo);
  if (!side) return '';
  const conids =
    side === 'Yes'
      ? (siblingConids.value.yesSiblings.get(conidYes) ?? [conidYes])
      : (siblingConids.value.noSiblings.get(conidNo) ?? [conidNo]);
  const size = positionSizeFor(conids);
  return `${size > 0 ? formatCompactNumber(size) : ''} ${side.toUpperCase()}`;
}

function getPartyTextClass(party: string): string {
  if (party === 'GOP') return 'text-republican';
  if (party === 'DEM') return 'text-democrat';
  if (party === 'GRN') return 'text-green';
  return 'text-independent';
}

const FORECASTX_KEYS = ['forecastx', 'forecastex'];

function sortExchangesForecastFirst(exchanges: string[]): string[] {
  const fx = exchanges.filter((e) => FORECASTX_KEYS.includes(e.toLowerCase()));
  const other = exchanges.filter((e) => !FORECASTX_KEYS.includes(e.toLowerCase()));
  return [...fx, ...other];
}

function exchangeZIndex(exchanges: string[], index: number): number {
  return exchanges.length - index;
}
</script>

<template>
  <div
    role="grid"
    aria-label="Election contracts"
    :class="[
      'election-table-v2 mx-auto grid w-full max-w-[1440px] min-w-[960px] max-[1279.98px]:min-w-0',
      { 'no-position-col': !userState.authenticated },
    ]"
  >
    <!-- Column headers (desktop only) -->
    <div role="row" class="!visible grid col-span-full [grid-template-columns:subgrid] max-[1279.98px]:hidden">
      <div
        role="columnheader"
        class="flex items-center px-3 py-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-on-surface whitespace-nowrap select-none"
      >
        {{ $t('race') }}
      </div>
      <div
        role="columnheader"
        class="flex items-center px-3 py-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-on-surface whitespace-nowrap select-none"
      >
        {{ $t('candidate') }}
      </div>
      <div
        role="columnheader"
        class="flex items-center px-3 py-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-on-surface whitespace-nowrap select-none"
      >
        {{ $t('action') }}
      </div>
      <div
        v-if="userState.authenticated"
        role="columnheader"
        class="flex items-center px-3 py-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-on-surface whitespace-nowrap select-none"
      >
        {{ $t('position') }}
      </div>
      <div
        role="columnheader"
        class="flex items-center justify-center px-1 py-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-on-surface whitespace-nowrap select-none"
      >
        {{ $t('openInt') }}
      </div>
      <div
        role="columnheader"
        class="flex items-center justify-center py-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-on-surface whitespace-nowrap select-none"
      >
        {{ $t('exch') }}
      </div>
    </div>

    <!-- Loading skeleton -->
    <template v-if="loading && products.length === 0">
      <div
        v-for="i in 4"
        :key="`skeleton-${i}`"
        class="grid col-span-full [grid-template-columns:subgrid] grid-rows-[auto]"
      >
        <div class="grid col-span-full [grid-template-columns:subgrid]">
          <div class="col-span-full flex items-center gap-4 px-3 py-6">
            <div class="h-4 w-48 rounded bg-surface-hover" />
          </div>
        </div>
        <div v-for="j in 2" :key="`skeleton-cand-${i}-${j}`" class="grid col-span-full [grid-template-columns:subgrid]">
          <div />
          <div class="flex items-center gap-3 px-3 py-3">
            <div class="h-10 w-10 rounded-full bg-surface-hover" />
            <div class="h-4 w-32 rounded bg-surface-hover" />
          </div>
          <div class="flex items-center gap-2 px-3 py-3">
            <div class="h-8 w-16 rounded-lg bg-surface-hover" />
            <div class="h-8 w-16 rounded-lg bg-surface-hover" />
          </div>
          <div class="px-3 py-3">
            <div class="h-4 w-12 rounded bg-surface-hover" />
          </div>
          <div class="px-3 py-3">
            <div class="h-4 w-12 rounded bg-surface-hover" />
          </div>
          <div class="px-3 py-3">
            <div class="h-4 w-8 rounded bg-surface-hover" />
          </div>
        </div>
      </div>
    </template>

    <!-- ================================================================= -->
    <!-- State-grouped mode (All By State): state headers + nested races   -->
    <!-- ================================================================= -->
    <template v-if="stateData">
      <template v-for="stateHeader in stateHeaders" :key="stateHeader.stateCode">
        <!-- State header row — always visible, click to expand/collapse -->
        <!-- Scroll anchor for A-Z scrubber (mobile only) -->
        <span
          v-if="isMobileScrubber"
          :id="`state-anchor-${stateHeader.stateCode}`"
          class="col-span-full h-0 overflow-hidden"
        />
        <div
          class="state-header-row col-span-full flex cursor-pointer items-center gap-3 border-t border-surface-border px-3 py-2"
          @click="toggleStateExpansion(stateHeader.stateCode)"
        >
          <button
            class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-on-surface-tertiary transition-colors hover:bg-surface-hover hover:text-on-surface"
          >
            <i
              :class="expandedStates.has(stateHeader.stateCode) ? 'ibi-chevron-down' : 'ibi-chevron-right'"
              class="text-base"
            />
          </button>
          <div
            class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-black text-[10px] font-black text-white shadow-sm"
          >
            {{ stateHeader.stateCode }}
          </div>
          <span class="text-base font-bold text-on-surface">{{ stateHeader.stateName }}</span>
          <span class="rounded-full bg-surface-hover px-2 py-0.5 text-xs font-semibold text-muted-color">
            {{ stateHeader.raceCount }} {{ stateHeader.raceCount === 1 ? 'race' : 'races' }}
          </span>
        </div>

        <!-- Race groups inside this state (only when expanded) -->
        <template v-if="expandedStates.has(stateHeader.stateCode)">
          <div
            v-for="raceId in stateHeader.raceIds"
            :key="raceId"
            :ref="(el: any) => trackRaceElement(raceId, el)"
            class="grid col-span-full [grid-template-columns:subgrid] grid-rows-[auto] max-[1279.98px]:rounded-[8px] max-[1279.98px]:border max-[1279.98px]:border-surface-border max-[1279.98px]:mb-3"
            :style="{
              '--visible-rows': raceRowSpan(raceId),
            }"
          >
            <template v-if="renderedRaceIds.has(raceId)">
              <!-- Race info: top block (tablet/mobile only) -->
              <div
                class="hidden !visible max-[1279.98px]:block col-span-full cursor-pointer px-3 py-3"
                @click="emit('select-race', groupedByRace.get(raceId)?.[0]?.productConid ?? 0)"
              >
                <h6
                  class="text-sm leading-tight text-on-surface line-clamp-2 max-[600px]:truncate max-[600px]:line-clamp-none"
                >
                  {{ groupedByRace.get(raceId)?.[0]?.raceName }}
                </h6>
                <div class="race-meta mt-1 flex items-center gap-6 text-sm">
                  <span class="flex items-center gap-0.5">
                    <i class="ibi-calendar" />
                    {{ formatRaceDate(groupedByRace.get(raceId)?.[0]?.lastTradeTime ?? 0) }}
                  </span>
                  <span class="flex items-center gap-0.5">
                    <i class="ibi-arrow-increase-profit" />
                    {{ formatRaceOI(raceId) }}
                  </span>
                  <span class="flex items-center">
                    <template v-if="renderedRaceIds.has(raceId)">
                      <AvatarGroup v-if="(raceExchanges.get(raceId)?.length ?? 0) > 1">
                        <Avatar
                          v-for="(ex, i) in sortExchangesForecastFirst(raceExchanges.get(raceId)!)"
                          :key="ex"
                          :image="getExchangeIconUrl(ex)"
                          :aria-label="ex"
                          shape="circle"
                          class="!w-5 !h-5"
                          :style="{ zIndex: exchangeZIndex(raceExchanges.get(raceId)!, i) }"
                        />
                      </AvatarGroup>
                      <ExchangeBadge
                        v-else-if="raceExchanges.get(raceId)?.[0]"
                        :exchange="raceExchanges.get(raceId)![0]"
                        size="md"
                      />
                    </template>
                    <div v-else class="w-8 h-8 rounded-full bg-surface-hover" />
                  </span>
                </div>
              </div>

              <!-- Race info: side column cell (desktop only) -->
              <div
                class="!visible flex items-start max-[1279.98px]:hidden border-t border-surface-border"
                :style="{
                  gridRow: `span ${raceRowSpan(raceId)}`,
                }"
              >
                <div
                  class="cursor-pointer px-3 py-3"
                  @click="emit('select-race', groupedByRace.get(raceId)?.[0]?.productConid ?? 0)"
                >
                  <h6 class="text-sm leading-tight text-on-surface line-clamp-2">
                    {{ groupedByRace.get(raceId)?.[0]?.raceName }}
                  </h6>
                  <div class="race-meta mt-1 flex items-center gap-6 text-sm">
                    <span class="flex items-center gap-0.5">
                      <i class="ibi-calendar" />
                      {{ formatRaceDate(groupedByRace.get(raceId)?.[0]?.lastTradeTime ?? 0) }}
                    </span>
                    <span class="flex items-center gap-0.5">
                      <i class="ibi-arrow-increase-profit" />
                      {{ formatRaceOI(raceId) }}
                    </span>
                    <span class="flex items-center">
                      <template v-if="renderedRaceIds.has(raceId)">
                        <AvatarGroup v-if="(raceExchanges.get(raceId)?.length ?? 0) > 1">
                          <Avatar
                            v-for="(ex, i) in sortExchangesForecastFirst(raceExchanges.get(raceId)!)"
                            :key="ex"
                            :image="getExchangeIconUrl(ex)"
                            :aria-label="ex"
                            shape="circle"
                            class="!w-5 !h-5"
                            :style="{ zIndex: exchangeZIndex(raceExchanges.get(raceId)!, i) }"
                          />
                        </AvatarGroup>
                        <ExchangeBadge
                          v-else-if="raceExchanges.get(raceId)?.[0]"
                          :exchange="raceExchanges.get(raceId)![0]"
                          size="md"
                        />
                      </template>
                      <div v-else class="w-5 h-5 rounded-full bg-surface-hover" />
                    </span>
                  </div>
                </div>
              </div>

              <!-- Candidate rows -->
              <template v-for="candidate in raceViewData.get(raceId)?.visible" :key="candidate.conid_yes">
                <div
                  role="row"
                  :class="[
                    'candidate-row grid [grid-template-columns:subgrid] min-h-9 transition-colors hover:bg-surface-hover border-t border-surface-border',
                    {
                      'single-candidate':
                        raceViewData.get(raceId)?.visible.length === 1 && !raceViewData.get(raceId)?.hasMore,
                    },
                  ]"
                >
                  <!-- Candidate info -->
                  <div
                    role="gridcell"
                    class="flex items-center gap-2.5 px-3 py-2 min-w-0 cursor-pointer"
                    @click="
                      emit('select-candidate', { productConid: candidate.productConid, conidYes: candidate.conid_yes })
                    "
                  >
                    <CandidateAvatar
                      v-if="renderedRaceIds.has(raceId)"
                      :conid="candidate.conid_yes"
                      :candidate-name="candidate.candidateName"
                      :party-code="candidate.party"
                      size="sm"
                      rounded
                    />
                    <div v-else class="w-5 h-5 shrink-0 rounded-full bg-surface-hover" />
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-1">
                        <h6 :class="['truncate', getPartyTextClass(candidate.party)]">
                          {{ candidate.candidateName }}
                        </h6>
                        <a
                          v-if="getBallotpediaUrl(candidate.candidateName)"
                          :href="getBallotpediaUrl(candidate.candidateName)!"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="!visible shrink-0 ml-1 flex h-5 w-5 items-center justify-center rounded-full text-muted-color hover:text-on-surface transition-colors max-[600px]:hidden"
                          @click.stop.prevent="
                            openUrl(getBallotpediaUrl(candidate.candidateName)!, { target: '_blank' })
                          "
                        >
                          <i class="ibi-arrow-up-right text-sm" />
                        </a>
                        <button
                          class="!visible shrink-0 cursor-pointer flex h-5 w-5 items-center justify-center rounded-full text-on-surface/40 transition-colors hover:text-on-surface hover:bg-surface-hover max-[600px]:hidden"
                          :title="$t('share')"
                          @click.stop="emit('share', candidate.conid_yes)"
                        >
                          <i class="ibi-share-send text-muted-color" />
                        </button>
                      </div>
                      <div class="hidden !visible max-[600px]:flex items-center gap-2 text-xs">
                        <span class="flex items-center gap-0.5">
                          <i class="ibi-arrow-increase-profit" />
                          {{ formatOI(candidate.conid_yes) }}
                        </span>
                        <span
                          v-if="getPositionDisplay(candidate.conid_yes, candidate.conid_no)"
                          class="rounded px-1 py-0.5 text-[9px] tabular-nums text-white leading-none"
                          :class="
                            getPositionSide(candidate.conid_yes, candidate.conid_no) === 'Yes'
                              ? 'bg-positive'
                              : 'bg-negative'
                          "
                        >
                          {{ getPositionDisplay(candidate.conid_yes, candidate.conid_no) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Action cell -->
                  <div role="gridcell" class="!visible flex items-center gap-1.5 px-3 py-2 max-[600px]:hidden">
                    <template v-if="renderedRaceIds.has(raceId)">
                      <Button
                        severity="secondary"
                        :ib-variant="isYesCloseState(candidate.conid_no) ? 'close_order' : 'yes'"
                        size="small"
                        :icon="!userState.authenticated ? 'ibi-lock-closed' : undefined"
                        icon-pos="right"
                        class="!flex-1 !tabular-nums !text-[13px] !font-semibold !px-2 !py-1.5 !whitespace-nowrap"
                        @click.stop="
                          openOrderTicket(
                            isYesCloseState(candidate.conid_no) ? candidate.conid_no : candidate.conid_yes,
                            candidate.conid_yes,
                            isYesCloseState(candidate.conid_no) ? 'close' : undefined,
                            $event,
                          )
                        "
                      >
                        <i v-if="isYesCloseState(candidate.conid_no)" class="ibi-close-x-filled text-xs" />
                        <span
                          >{{ isYesCloseState(candidate.conid_no) ? '' : 'Yes '
                          }}{{ formatPrice(candidate.conid_yes) }}</span
                        >
                      </Button>
                      <Button
                        severity="secondary"
                        :ib-variant="isNoCloseState(candidate.conid_yes) ? 'close_order' : 'no'"
                        size="small"
                        :icon="!userState.authenticated ? 'ibi-lock-closed' : undefined"
                        icon-pos="right"
                        class="!flex-1 !tabular-nums !text-[13px] !font-semibold !px-2 !py-1.5 !whitespace-nowrap"
                        @click.stop="
                          openOrderTicket(
                            isNoCloseState(candidate.conid_yes) ? candidate.conid_yes : candidate.conid_no,
                            candidate.conid_yes,
                            isNoCloseState(candidate.conid_yes) ? 'close' : undefined,
                            $event,
                          )
                        "
                      >
                        <i v-if="isNoCloseState(candidate.conid_yes)" class="ibi-close-x-filled text-xs" />
                        <span
                          >{{ isNoCloseState(candidate.conid_yes) ? '' : 'No '
                          }}{{ formatPrice(candidate.conid_no) }}</span
                        >
                      </Button>
                    </template>
                    <template v-else>
                      <div class="flex-1 h-8 rounded-lg bg-surface-hover" />
                      <div class="flex-1 h-8 rounded-lg bg-surface-hover" />
                    </template>
                  </div>

                  <!-- Mobile: Yes button -->
                  <div role="gridcell" class="hidden !visible max-[600px]:flex items-center py-2 pr-1">
                    <Button
                      v-if="renderedRaceIds.has(raceId)"
                      severity="secondary"
                      :ib-variant="isYesCloseState(candidate.conid_no) ? 'close_order' : 'yes'"
                      size="small"
                      :icon="!userState.authenticated ? 'ibi-lock-closed' : undefined"
                      icon-pos="right"
                      class="!w-full !min-w-[4.25rem] !tabular-nums !text-[12px] !font-semibold !px-1.5 !py-1.5 !whitespace-nowrap"
                      @click.stop="
                        openOrderTicket(
                          isYesCloseState(candidate.conid_no) ? candidate.conid_no : candidate.conid_yes,
                          candidate.conid_yes,
                          isYesCloseState(candidate.conid_no) ? 'close' : undefined,
                          $event,
                        )
                      "
                    >
                      <i v-if="isYesCloseState(candidate.conid_no)" class="ibi-close-x-filled text-xs" />
                      <span
                        >{{ isYesCloseState(candidate.conid_no) ? '' : 'Y '
                        }}{{ formatPrice(candidate.conid_yes) }}</span
                      >
                    </Button>
                    <div v-else class="h-8 w-16 rounded-lg bg-surface-hover" />
                  </div>

                  <!-- Mobile: No button -->
                  <div role="gridcell" class="hidden !visible max-[600px]:flex items-center py-2 pr-1">
                    <Button
                      v-if="renderedRaceIds.has(raceId)"
                      severity="secondary"
                      :ib-variant="isNoCloseState(candidate.conid_yes) ? 'close_order' : 'no'"
                      size="small"
                      :icon="!userState.authenticated ? 'ibi-lock-closed' : undefined"
                      icon-pos="right"
                      class="!w-full !min-w-[4.25rem] !tabular-nums !text-[12px] !font-semibold !px-1.5 !py-1.5 !whitespace-nowrap"
                      @click.stop="
                        openOrderTicket(
                          isNoCloseState(candidate.conid_yes) ? candidate.conid_yes : candidate.conid_no,
                          candidate.conid_yes,
                          isNoCloseState(candidate.conid_yes) ? 'close' : undefined,
                          $event,
                        )
                      "
                    >
                      <i v-if="isNoCloseState(candidate.conid_yes)" class="ibi-close-x-filled text-xs" />
                      <span
                        >{{ isNoCloseState(candidate.conid_yes) ? '' : 'N '
                        }}{{ formatPrice(candidate.conid_no) }}</span
                      >
                    </Button>
                    <div v-else class="h-8 w-16 rounded-lg bg-surface-hover" />
                  </div>

                  <!-- Mobile: Kebab menu -->
                  <div role="gridcell" class="hidden !visible max-[600px]:flex items-center py-2 pe-2">
                    <button
                      class="flex h-6 w-5 cursor-pointer items-center justify-center rounded text-muted-color hover:text-on-surface"
                      @click.stop="openCandidateMenu($event, candidate.candidateName, candidate.conid_yes)"
                    >
                      <i class="ibi-menu-more-kebab-vertical text-base" />
                    </button>
                  </div>

                  <!-- Position -->
                  <div
                    v-if="userState.authenticated"
                    role="gridcell"
                    class="!visible flex items-center px-3 py-2 text-sm tabular-nums max-[600px]:hidden"
                    :class="
                      getPositionSide(candidate.conid_yes, candidate.conid_no) === 'Yes'
                        ? 'text-positive'
                        : getPositionSide(candidate.conid_yes, candidate.conid_no) === 'No'
                          ? 'text-negative'
                          : 'text-on-surface'
                    "
                  >
                    {{ getPositionDisplay(candidate.conid_yes, candidate.conid_no) }}
                  </div>

                  <!-- Open Interest -->
                  <div
                    role="gridcell"
                    class="!visible flex items-center justify-center gap-0.5 px-3 py-2 text-sm tabular-nums text-on-surface max-[600px]:hidden"
                  >
                    <i class="oi-icon ibi-arrow-increase-profit text-xs" />
                    {{ formatOI(candidate.conid_yes) }}
                  </div>

                  <!-- Exchange badge -->
                  <div
                    role="gridcell"
                    class="!visible flex items-center justify-center py-2 text-sm tabular-nums text-on-surface max-[600px]:hidden"
                  >
                    <template v-if="renderedRaceIds.has(raceId)">
                      <AvatarGroup v-if="(productLookups.candidateExchanges[candidate.conid_yes]?.length ?? 0) > 1">
                        <Avatar
                          v-for="(ex, i) in sortExchangesForecastFirst(
                            productLookups.candidateExchanges[candidate.conid_yes],
                          )"
                          :key="ex"
                          :image="getExchangeIconUrl(ex)"
                          :aria-label="ex"
                          shape="circle"
                          class="!w-5 !h-5"
                          :style="{ zIndex: exchangeZIndex(productLookups.candidateExchanges[candidate.conid_yes], i) }"
                        />
                      </AvatarGroup>
                      <ExchangeBadge
                        v-else-if="productLookups.exchangeMap[candidate.conid_yes]"
                        :exchange="productLookups.exchangeMap[candidate.conid_yes]"
                        size="sm"
                      />
                    </template>
                    <div v-else class="w-5 h-5 rounded-full bg-surface-hover" />
                  </div>
                </div>
              </template>

              <!-- See more contracts: deep link to the race on Prediction Markets -->
              <div
                v-if="raceViewData.get(raceId)?.hasMore"
                class="more-candidates-row border-t border-surface-border"
                role="link"
                tabindex="0"
                @click="openPredictionMarkets(raceId)"
                @keydown.enter="openPredictionMarkets(raceId)"
              >
                <div class="flex items-center gap-2 px-3 py-2 cursor-pointer text-xs text-on-surface transition-colors">
                  <AvatarGroup v-if="raceViewData.get(raceId)?.extras.length">
                    <Avatar
                      v-for="extra in raceViewData.get(raceId)?.extras.slice(0, 4)"
                      :key="extra.conid_yes"
                      :image="`/tws.proxy/public/icons/forecastx?conid=${extra.conid_yes}&fallback=true`"
                      :aria-label="extra.candidateName"
                      shape="circle"
                      class="!w-5 !h-5 !text-xs"
                    />
                  </AvatarGroup>
                  <span class="font-medium">{{ $t('seeMoreAtPredictionMarkets') }}</span>
                  <span aria-hidden="true" class="text-sm font-bold">&rarr;</span>
                </div>
              </div>
            </template>
            <div v-else class="col-span-full" :style="{ minHeight: racePlaceholderHeight(raceId) }" />
          </div>
        </template>
      </template>
    </template>

    <!-- ================================================================= -->
    <!-- Flat mode (single office): race groups without state headers       -->
    <!-- ================================================================= -->
    <template v-else>
      <template v-for="raceId in raceIds" :key="raceId">
        <!-- Scroll anchor for A-Z scrubber -->
        <span v-if="isMobileScrubber" :id="`race-anchor-${raceId}`" class="col-span-full h-0 overflow-hidden" />
        <div
          :ref="(el: any) => trackRaceElement(raceId, el)"
          class="grid col-span-full [grid-template-columns:subgrid] grid-rows-[auto] max-[1279.98px]:rounded-[8px] max-[1279.98px]:border max-[1279.98px]:border-surface-border max-[1279.98px]:mb-3"
          :style="{
            '--visible-rows': raceRowSpan(raceId),
          }"
        >
          <template v-if="renderedRaceIds.has(raceId)">
            <!-- Race info: top block (tablet/mobile only) -->
            <div
              class="hidden !visible max-[1279.98px]:block col-span-full cursor-pointer px-3 py-3"
              @click="handleRaceClick(raceId)"
            >
              <h6
                class="text-sm leading-tight text-on-surface line-clamp-2 max-[600px]:truncate max-[600px]:line-clamp-none"
              >
                {{ groupedByRace.get(raceId)?.[0]?.raceName }}
              </h6>
              <div class="race-meta mt-1 flex items-center gap-6 text-sm">
                <span class="flex items-center gap-0.5">
                  <i class="ibi-calendar" />
                  {{
                    formatRaceDate(
                      groupedByRace.get(raceId)?.[0]?.lastTradeTime ?? 0,
                      groupedByRace.get(raceId)?.[0]?.measuredPeriod,
                    )
                  }}
                </span>
                <span class="flex items-center gap-0.5">
                  <i class="ibi-arrow-increase-profit" />
                  {{ formatRaceOI(raceId) }}
                </span>
                <span class="flex items-center">
                  <template v-if="renderedRaceIds.has(raceId)">
                    <AvatarGroup v-if="(raceExchanges.get(raceId)?.length ?? 0) > 1">
                      <Avatar
                        v-for="(ex, i) in sortExchangesForecastFirst(raceExchanges.get(raceId)!)"
                        :key="ex"
                        :image="getExchangeIconUrl(ex)"
                        :aria-label="ex"
                        shape="circle"
                        class="!w-5 !h-5"
                        :style="{ zIndex: exchangeZIndex(raceExchanges.get(raceId)!, i) }"
                      />
                    </AvatarGroup>
                    <ExchangeBadge
                      v-else-if="raceExchanges.get(raceId)?.[0]"
                      :exchange="raceExchanges.get(raceId)![0]"
                      size="md"
                    />
                  </template>
                  <div v-else class="w-5 h-5 rounded-full bg-surface-hover" />
                </span>
              </div>
            </div>

            <!-- Race info: side column cell (desktop only) -->
            <div
              class="!visible flex items-start max-[1279.98px]:hidden border-t border-surface-border"
              :style="{
                gridRow: `span ${raceRowSpan(raceId)}`,
              }"
            >
              <div class="cursor-pointer px-3 py-3" @click="handleRaceClick(raceId)">
                <h6 class="text-sm leading-tight text-on-surface line-clamp-2">
                  {{ groupedByRace.get(raceId)?.[0]?.raceName }}
                </h6>
                <div class="race-meta mt-1 flex items-center gap-6 text-sm">
                  <span class="flex items-center gap-0.5">
                    <i class="ibi-calendar" />
                    {{
                      formatRaceDate(
                        groupedByRace.get(raceId)?.[0]?.lastTradeTime ?? 0,
                        groupedByRace.get(raceId)?.[0]?.measuredPeriod,
                      )
                    }}
                  </span>
                  <span class="flex items-center gap-0.5">
                    <i class="ibi-arrow-increase-profit" />
                    {{ formatRaceOI(raceId) }}
                  </span>
                  <span class="flex items-center">
                    <template v-if="renderedRaceIds.has(raceId)">
                      <AvatarGroup v-if="(raceExchanges.get(raceId)?.length ?? 0) > 1">
                        <Avatar
                          v-for="(ex, i) in sortExchangesForecastFirst(raceExchanges.get(raceId)!)"
                          :key="ex"
                          :image="getExchangeIconUrl(ex)"
                          :aria-label="ex"
                          shape="circle"
                          class="!w-5 !h-5"
                          :style="{ zIndex: exchangeZIndex(raceExchanges.get(raceId)!, i) }"
                        />
                      </AvatarGroup>
                      <ExchangeBadge
                        v-else-if="raceExchanges.get(raceId)?.[0]"
                        :exchange="raceExchanges.get(raceId)![0]"
                        size="md"
                      />
                    </template>
                    <div v-else class="w-5 h-5 rounded-full bg-surface-hover" />
                  </span>
                </div>
              </div>
            </div>

            <!-- Candidate rows -->
            <template v-for="candidate in raceViewData.get(raceId)?.visible" :key="candidate.conid_yes">
              <div
                role="row"
                :class="[
                  'candidate-row grid [grid-template-columns:subgrid] min-h-9 transition-colors hover:bg-surface-hover border-t border-surface-border',
                  {
                    'single-candidate':
                      raceViewData.get(raceId)?.visible.length === 1 && !raceViewData.get(raceId)?.hasMore,
                  },
                ]"
              >
                <!-- Candidate info -->
                <div
                  role="gridcell"
                  class="flex items-center gap-2.5 px-3 py-2 min-w-0 cursor-pointer"
                  @click="handleCandidateClick(candidate)"
                >
                  <CandidateAvatar
                    v-if="renderedRaceIds.has(raceId)"
                    :conid="candidate.conid_yes"
                    :candidate-name="candidate.candidateName"
                    :party-code="candidate.party"
                    size="sm"
                    rounded
                  />
                  <div v-else class="w-5 h-5 shrink-0 rounded-full bg-surface-hover" />
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-1">
                      <h6 :class="['truncate', getPartyTextClass(candidate.party)]">
                        {{ candidate.candidateName }}
                      </h6>
                      <a
                        v-if="getBallotpediaUrl(candidate.candidateName)"
                        :href="getBallotpediaUrl(candidate.candidateName)!"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="!visible shrink-0 ml-1 flex h-5 w-5 items-center justify-center rounded-full text-muted-color hover:text-on-surface transition-colors max-[600px]:hidden"
                        @click.stop.prevent="openUrl(getBallotpediaUrl(candidate.candidateName)!, { target: '_blank' })"
                      >
                        <i class="ibi-arrow-up-right text-sm" />
                      </a>
                      <button
                        class="!visible shrink-0 cursor-pointer flex h-5 w-5 items-center justify-center rounded-full text-on-surface/40 transition-colors hover:text-on-surface hover:bg-surface-hover max-[600px]:hidden"
                        title="Share"
                        @click.stop="emit('share', candidate.conid_yes)"
                      >
                        <i class="ibi-share-send text-muted-color" />
                      </button>
                    </div>
                    <!-- Mobile: position below name -->
                    <div class="hidden !visible max-[600px]:flex items-center gap-2 text-xs">
                      <span>
                        {{ formatOI(candidate.conid_yes) }}
                      </span>
                      <span
                        v-if="getPositionDisplay(candidate.conid_yes, candidate.conid_no)"
                        class="rounded px-1 py-0.5 text-[9px] tabular-nums text-white leading-none"
                        :class="
                          getPositionSide(candidate.conid_yes, candidate.conid_no) === 'Yes'
                            ? 'bg-positive'
                            : 'bg-negative'
                        "
                      >
                        {{ getPositionDisplay(candidate.conid_yes, candidate.conid_no) }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Action cell: combined Yes+No on desktop/tablet, separate cells on mobile -->
                <div role="gridcell" class="!visible flex items-center gap-1.5 px-3 py-2 max-[600px]:hidden">
                  <template v-if="renderedRaceIds.has(raceId)">
                    <Button
                      severity="secondary"
                      :ib-variant="isYesCloseState(candidate.conid_no) ? 'close_order' : 'yes'"
                      size="small"
                      :icon="!userState.authenticated ? 'ibi-lock-closed' : undefined"
                      icon-pos="right"
                      class="!flex-1 !tabular-nums !text-[13px] !font-semibold !px-2 !py-1.5 !whitespace-nowrap"
                      @click.stop="
                        openOrderTicket(
                          isYesCloseState(candidate.conid_no) ? candidate.conid_no : candidate.conid_yes,
                          candidate.conid_yes,
                          isYesCloseState(candidate.conid_no) ? 'close' : undefined,
                          $event,
                        )
                      "
                    >
                      <i v-if="isYesCloseState(candidate.conid_no)" class="ibi-close-x-filled text-xs" />
                      <span
                        >{{ isYesCloseState(candidate.conid_no) ? '' : 'Yes '
                        }}{{ formatPrice(candidate.conid_yes) }}</span
                      >
                    </Button>
                    <Button
                      severity="secondary"
                      :ib-variant="isNoCloseState(candidate.conid_yes) ? 'close_order' : 'no'"
                      size="small"
                      :icon="!userState.authenticated ? 'ibi-lock-closed' : undefined"
                      icon-pos="right"
                      class="!flex-1 !tabular-nums !text-[13px] !font-semibold !px-2 !py-1.5 !whitespace-nowrap"
                      @click.stop="
                        openOrderTicket(
                          isNoCloseState(candidate.conid_yes) ? candidate.conid_yes : candidate.conid_no,
                          candidate.conid_yes,
                          isNoCloseState(candidate.conid_yes) ? 'close' : undefined,
                          $event,
                        )
                      "
                    >
                      <i v-if="isNoCloseState(candidate.conid_yes)" class="ibi-close-x-filled text-xs" />
                      <span
                        >{{ isNoCloseState(candidate.conid_yes) ? '' : 'No '
                        }}{{ formatPrice(candidate.conid_no) }}</span
                      >
                    </Button>
                  </template>
                  <template v-else>
                    <div class="flex-1 h-8 rounded-lg bg-surface-hover" />
                    <div class="flex-1 h-8 rounded-lg bg-surface-hover" />
                  </template>
                </div>

                <!-- Mobile: Yes button (separate grid cell) -->
                <div role="gridcell" class="hidden !visible max-[600px]:flex items-center py-2 pr-1">
                  <Button
                    v-if="renderedRaceIds.has(raceId)"
                    severity="secondary"
                    :ib-variant="isYesCloseState(candidate.conid_no) ? 'close_order' : 'yes'"
                    size="small"
                    :icon="!userState.authenticated ? 'ibi-lock-closed' : undefined"
                    icon-pos="right"
                    class="!w-full !min-w-[4.25rem] !tabular-nums !text-[12px] !font-semibold !px-1.5 !py-1.5 !whitespace-nowrap"
                    @click.stop="
                      openOrderTicket(
                        isYesCloseState(candidate.conid_no) ? candidate.conid_no : candidate.conid_yes,
                        candidate.conid_yes,
                        isYesCloseState(candidate.conid_no) ? 'close' : undefined,
                        $event,
                      )
                    "
                  >
                    <i v-if="isYesCloseState(candidate.conid_no)" class="ibi-close-x-filled text-xs" />
                    <span
                      >{{ isYesCloseState(candidate.conid_no) ? '' : 'Y ' }}{{ formatPrice(candidate.conid_yes) }}</span
                    >
                  </Button>
                  <div v-else class="h-8 w-16 rounded-lg bg-surface-hover" />
                </div>

                <!-- Mobile: No button (separate grid cell) -->
                <div role="gridcell" class="hidden !visible max-[600px]:flex items-center py-2 pr-1">
                  <Button
                    v-if="renderedRaceIds.has(raceId)"
                    severity="secondary"
                    :ib-variant="isNoCloseState(candidate.conid_yes) ? 'close_order' : 'no'"
                    size="small"
                    :icon="!userState.authenticated ? 'ibi-lock-closed' : undefined"
                    icon-pos="right"
                    class="!w-full !min-w-[4.25rem] !tabular-nums !text-[12px] !font-semibold !px-1.5 !py-1.5 !whitespace-nowrap"
                    @click.stop="
                      openOrderTicket(
                        isNoCloseState(candidate.conid_yes) ? candidate.conid_yes : candidate.conid_no,
                        candidate.conid_yes,
                        isNoCloseState(candidate.conid_yes) ? 'close' : undefined,
                        $event,
                      )
                    "
                  >
                    <i v-if="isNoCloseState(candidate.conid_yes)" class="ibi-close-x-filled text-xs" />
                    <span
                      >{{ isNoCloseState(candidate.conid_yes) ? '' : 'N ' }}{{ formatPrice(candidate.conid_no) }}</span
                    >
                  </Button>
                  <div v-else class="h-8 w-16 rounded-lg bg-surface-hover" />
                </div>

                <!-- Mobile: Kebab menu -->
                <div role="gridcell" class="hidden !visible max-[600px]:flex items-center py-2 pe-2">
                  <button
                    class="flex h-6 w-5 cursor-pointer items-center justify-center rounded text-muted-color hover:text-on-surface"
                    @click.stop="openCandidateMenu($event, candidate.candidateName, candidate.conid_yes)"
                  >
                    <i class="ibi-menu-more-kebab-vertical text-base" />
                  </button>
                </div>

                <!-- Position (desktop + tablet only, authenticated only) -->
                <div
                  v-if="userState.authenticated"
                  role="gridcell"
                  class="!visible flex items-center px-3 py-2 text-sm tabular-nums max-[600px]:hidden"
                  :class="
                    getPositionSide(candidate.conid_yes, candidate.conid_no) === 'Yes'
                      ? 'text-positive'
                      : getPositionSide(candidate.conid_yes, candidate.conid_no) === 'No'
                        ? 'text-negative'
                        : 'text-on-surface'
                  "
                >
                  {{ getPositionDisplay(candidate.conid_yes, candidate.conid_no) }}
                </div>

                <!-- Open Interest (desktop + tablet only) -->
                <div
                  role="gridcell"
                  class="!visible flex items-center justify-center gap-0.5 px-3 py-2 text-sm font-semibold tabular-nums text-on-surface max-[600px]:hidden"
                >
                  <i class="oi-icon ibi-arrow-increase-profit text-xs" />
                  {{ formatOI(candidate.conid_yes) }}
                </div>

                <!-- Exchange badge (desktop + tablet only) -->
                <div
                  role="gridcell"
                  class="!visible flex items-center justify-center py-2 text-sm tabular-nums text-on-surface max-[600px]:hidden"
                >
                  <template v-if="renderedRaceIds.has(raceId)">
                    <AvatarGroup v-if="(productLookups.candidateExchanges[candidate.conid_yes]?.length ?? 0) > 1">
                      <Avatar
                        v-for="(ex, i) in sortExchangesForecastFirst(
                          productLookups.candidateExchanges[candidate.conid_yes],
                        )"
                        :key="ex"
                        :image="getExchangeIconUrl(ex)"
                        :aria-label="ex"
                        shape="circle"
                        class="!w-5 !h-5"
                        :style="{ zIndex: exchangeZIndex(productLookups.candidateExchanges[candidate.conid_yes], i) }"
                      />
                    </AvatarGroup>
                    <ExchangeBadge
                      v-else-if="productLookups.exchangeMap[candidate.conid_yes]"
                      :exchange="productLookups.exchangeMap[candidate.conid_yes]"
                      size="sm"
                    />
                  </template>
                  <div v-else class="w-5 h-5 rounded-full bg-surface-hover" />
                </div>
              </div>
            </template>

            <!-- See more contracts: deep link to the race on Prediction Markets -->
            <div
              v-if="raceViewData.get(raceId)?.hasMore"
              class="more-candidates-row border-t border-surface-border"
              role="link"
              tabindex="0"
              @click="openPredictionMarkets(raceId)"
              @keydown.enter="openPredictionMarkets(raceId)"
            >
              <div class="flex items-center gap-2 px-3 py-2 cursor-pointer text-xs text-on-surface transition-colors">
                <AvatarGroup v-if="raceViewData.get(raceId)?.extras.length">
                  <Avatar
                    v-for="extra in raceViewData.get(raceId)?.extras.slice(0, 4)"
                    :key="extra.conid_yes"
                    :image="`/tws.proxy/public/icons/forecastx?conid=${extra.conid_yes}&fallback=true`"
                    :aria-label="extra.candidateName"
                    shape="circle"
                    class="!w-5 !h-5 !text-xs"
                  />
                </AvatarGroup>
                <span class="font-medium">{{ $t('seeMoreAtPredictionMarkets') }}</span>
                <span aria-hidden="true" class="text-sm font-bold">&rarr;</span>
              </div>
            </div>
          </template>
          <div v-else class="col-span-full" :style="{ minHeight: racePlaceholderHeight(raceId) }" />
        </div>
      </template>
    </template>

    <!-- A-Z scrubber for mobile (only when sorted alphabetically) -->
    <SectionIndexScrubber
      v-if="props.sortBy !== 'open-interest'"
      :sections="scrubberSections"
      @navigate="handleScrubberNavigate"
    />

    <!-- Exchange selection drawer for multi-venue candidates -->
    <!-- Desktop: exchange popover for multi-venue candidates -->
    <StrikeExchangePopover
      ref="popoverRef"
      :contracts="popoverContracts"
      :strike-label="popoverStrikeLabel"
      :side="popoverSide"
      :intent="popoverIntent"
      @buy-yes="handlePopoverBuyYes"
      @buy-no="handlePopoverBuyNo"
      @close-yes="handlePopoverCloseYes"
      @close-no="handlePopoverCloseNo"
    />

    <!-- Mobile: exchange selection drawer for multi-venue candidates -->
    <StrikeExchangeDrawer
      v-model="drawerVisible"
      :strike-groups="drawerStrikeGroups"
      :market-name="drawerMarketName"
      expiration-label=""
      price-side="bid"
      :initial-strike="drawerInitialStrike"
      @buy-yes="handleDrawerBuyYes"
      @buy-no="handleDrawerBuyNo"
      @close-yes="handleDrawerCloseYes"
      @close-no="handleDrawerCloseNo"
    />

    <SpeedBumpDrawer
      v-model:visible="speedBump.visible.value"
      :config="speedBump.config.value"
      @action="speedBump.handleAction"
    />

    <Menu
      ref="candidateMenuRef"
      :model="candidateMenuItems"
      :popup="true"
      :pt="{ root: '!min-w-[8rem]', item: '!text-sm' }"
    />
  </div>
</template>

<style scoped>
/* Desktop (>= 1280px): 6-column grid */
.election-table-v2 {
  grid-template-columns:
    minmax(200px, 1fr)
    minmax(200px, 1fr)
    minmax(192px, 1fr)
    minmax(72px, 0.4fr)
    minmax(56px, 0.4fr)
    32px;
}

/* Desktop: drop Position column when not authenticated */
.election-table-v2.no-position-col {
  grid-template-columns:
    minmax(200px, 1fr)
    minmax(200px, 1fr)
    minmax(192px, 1fr)
    minmax(56px, 0.4fr)
    32px;
}

/* Tablet (600px–1279px): no Race side column */
@media (width < 1280px) {
  .election-table-v2 {
    min-width: 0;
    grid-template-columns:
      minmax(145px, 1fr)
      minmax(140px, 1fr)
      minmax(48px, max-content)
      minmax(48px, max-content)
      32px;
  }

  /* Tablet: drop Position column when not authenticated */
  .election-table-v2.no-position-col {
    grid-template-columns:
      minmax(145px, 1fr)
      minmax(140px, 1fr)
      minmax(48px, max-content)
      32px;
  }
}

/* Mobile (<= 600px): 4 columns — candidate info + 2 buttons + kebab */
@media (width < 600px) {
  .election-table-v2,
  .election-table-v2.no-position-col {
    grid-template-columns: minmax(0, 1fr) auto auto 20px;
  }
}

/* Candidate row grid-column: skip Race col on desktop, span all on tablet/mobile */
.candidate-row {
  grid-column: 2 / -1;
}

.candidate-row > [role='gridcell'] {
  padding-top: 3px;
  padding-bottom: 3px;
}

.candidate-row:last-child > [role='gridcell'] {
  padding-bottom: 0.5rem;
}

.candidate-row.single-candidate > [role='gridcell'] {
  align-items: flex-start;
  padding-top: 0.75rem;
}

.more-candidates-row {
  grid-column: 2 / -1;
}

.more-candidates-row:hover {
  background: var(--p-highlight-background);
}

@media (width < 1280px) {
  .candidate-row,
  .more-candidates-row {
    grid-column: 1 / -1;
  }
}

/* State header row: muted ground background */
.state-header-row {
  background: var(--p-surface-ground);
}

/* Race metadata: fixed-width date + OI spans so exchange icons align across rows */
.race-meta > span:first-child {
  min-width: 5.5rem;
}

.race-meta > span:nth-child(2) {
  min-width: 3.5rem;
}

@media (width < 600px) {
  .race-meta {
    gap: 0.75rem !important;
    font-size: 0.75rem;
  }

  .race-meta > span:first-child {
    min-width: 0;
  }

  .race-meta > span:nth-child(2) {
    min-width: 0;
  }
}

@media (width >= 1280px) {
  .oi-icon {
    display: none;
  }
}
</style>
<!-- last modified by AI tools on 2026-10-09 -->
